import { z } from 'zod';
import {
  BaseRequestSchema,
  LabelRefSchema,
  ProviderMessageIdSchema,
  UuidSchema,
} from './primitives.ts';

export const ModifyMessageSchema = BaseRequestSchema.extend({
  action: z.literal('modify_message'),
  messageId: ProviderMessageIdSchema,
  addLabels: z.array(LabelRefSchema).default([]),
  removeLabels: z.array(LabelRefSchema).default([]),
  /**
   * Whether this move deliberately ends a snooze over the same mail. Current
   * clients always send it; a request without it is a legacy client, refused
   * while a snooze operation covers its mail (see
   * docs/plans/2026-10-28-feat-parity-r8-snooze-lifecycle-plan.md).
   */
  supersedeSnooze: z.boolean().optional(),
  /**
   * When true, the adapter expands `messageId` to every message in the same
   * thread (via `email_metadata.thread_id`, scoped to the caller's account)
   * and applies the same label delta to each. Default false — single-message
   * modify. Mirrors `delete_message` and `batch_modify_messages` thread
   * expansion semantics.
   */
  applyToThread: z.boolean().default(false),
});

export const DeleteMessageSchema = BaseRequestSchema.extend({
  action: z.literal('delete_message'),
  messageId: ProviderMessageIdSchema,
  /**
   * Whether this move deliberately ends a snooze over the same mail. Current
   * clients always send it; a request without it is a legacy client, refused
   * while a snooze operation covers its mail (see
   * docs/plans/2026-10-28-feat-parity-r8-snooze-lifecycle-plan.md).
   */
  supersedeSnooze: z.boolean().optional(),
  /**
   * When true, the adapter expands `messageId` to every message in the same
   * thread (via `email_metadata.thread_id`) and deletes each. Mirrors legacy
   * `handleDeleteMessage` semantics so the redirector path preserves "delete
   * thread" UX. Default false — single-message delete.
   */
  applyToThread: z.boolean().default(false),
});

export const TrashMessageSchema = BaseRequestSchema.extend({
  action: z.literal('trash_message'),
  messageId: ProviderMessageIdSchema,
  /**
   * Whether this move deliberately ends a snooze over the same mail. Current
   * clients always send it; a request without it is a legacy client, refused
   * while a snooze operation covers its mail (see
   * docs/plans/2026-10-28-feat-parity-r8-snooze-lifecycle-plan.md).
   */
  supersedeSnooze: z.boolean().optional(),
});

export const UntrashMessageSchema = BaseRequestSchema.extend({
  action: z.literal('untrash_message'),
  messageId: ProviderMessageIdSchema,
});

export const CreateLabelSchema = BaseRequestSchema.extend({
  action: z.literal('create_label'),
  name: z.string().min(1).max(256),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
});

export const BatchModifyMessagesSchema = BaseRequestSchema.extend({
  action: z.literal('batch_modify_messages'),
  messageIds: z.array(ProviderMessageIdSchema).min(1).max(1000),
  addLabels: z.array(LabelRefSchema).default([]),
  removeLabels: z.array(LabelRefSchema).default([]),
  /**
   * Whether this move deliberately ends a snooze over the same mail. Current
   * clients always send it; a request without it is a legacy client, refused
   * while a snooze operation covers its mail (see
   * docs/plans/2026-10-28-feat-parity-r8-snooze-lifecycle-plan.md).
   */
  supersedeSnooze: z.boolean().optional(),
  /**
   * When true, the adapter expands `messageIds` to include every message in
   * their threads (via `email_metadata.thread_id`) before applying the label
   * delta. Default false — operate only on the provided IDs.
   */
  applyToThread: z.boolean().default(false),
});

/**
 * Dedicated archive action — adapters implement provider-correct archive
 * semantics. The pre-existing `batch_modify_messages{removeLabels:['INBOX']}`
 * translation was a silent no-op on Outlook because categories are not
 * folders and "INBOX" is not a Graph category. This action routes to:
 *   - Gmail: batchModify with `removeLabelIds: ['INBOX']` (the native
 *     "archive" — archived messages are just messages without the INBOX
 *     label).
 *   - Outlook: `/me/messages/{id}/move` with `destinationId = <Archive
 *     folder id>` — the Archive folder is a well-known mailbox folder,
 *     resolved once per adapter call and cached within the request.
 */
export const ArchiveMessageSchema = BaseRequestSchema.extend({
  action: z.literal('archive_message'),
  messageIds: z.array(ProviderMessageIdSchema).min(1).max(1000),
  /**
   * Whether this move deliberately ends a snooze over the same mail. Current
   * clients always send it; a request without it is a legacy client, refused
   * while a snooze operation covers its mail (see
   * docs/plans/2026-10-28-feat-parity-r8-snooze-lifecycle-plan.md).
   */
  supersedeSnooze: z.boolean().optional(),
  /**
   * When true, the adapter expands `messageIds` to include every message
   * in their threads before archiving. Default TRUE — the legacy archive
   * button in every known caller targets the full thread, and keeping
   * that default matches pre-facade behavior.
   */
  applyToThread: z.boolean().default(true),
  /**
   * Optional RFC5322 `internetMessageId` per entry of `messageIds`, keyed by
   * the provider message id. A stored Graph immutable id can go dead (the
   * message was restored, moved across mailboxes, or the id predates the
   * immutable-id regime), after which every `/move` on it 404s forever. The
   * `internetMessageId` is stable across those events, so supplying it lets
   * the Outlook adapter re-resolve the live id and complete the archive
   * instead of failing the whole thread. Gmail ignores the field.
   */
  internetMessageIdHints: z.record(z.string().min(1)).optional(),
});

/**
 * Executes the newsletter-unsubscribe action for one email: RFC 8058
 * one-click POST, external https link hand-off, or system-generated mailto
 * send (ADR 0087 — an unsubscribe action, never a user send). Like
 * `get_unsubscribe_info`, the input keys on the `email_metadata` row id and
 * the owning account is derived server-side, so `accountId` is omitted from
 * the wire schema — a caller cannot smuggle a sibling account id. The
 * eligibility gate is re-derived server-side from persisted columns; the
 * client's `get_unsubscribe_info` verdict is advisory only.
 */
export const UnsubscribeSchema = BaseRequestSchema.extend({
  action: z.literal('unsubscribe'),
  emailId: UuidSchema,
}).omit({ accountId: true });

/**
 * Withdraws the active unsubscribe request for the email's sender (status
 * `superseded`): the member resubscribed on the sender's site, or wants to ask
 * again because the sender kept mailing. Same shape and derivation as
 * `unsubscribe` - the email row identifies the sender and the owning account
 * server-side; the audit row is never deleted, only closed.
 */
export const ResetUnsubscribeSchema = BaseRequestSchema.extend({
  action: z.literal('reset_unsubscribe'),
  emailId: UuidSchema,
}).omit({ accountId: true });

/**
 * Snooze one email: hides its correspondence locally at once and, where the
 * snooze lifecycle is enabled, takes the mail out of the native inbox until
 * the snooze ends (docs/plans/2026-10-28-feat-parity-r8-snooze-lifecycle-plan.md).
 */
export const SnoozeMessageSchema = BaseRequestSchema.extend({
  action: z.literal('snooze_message'),
  messageId: ProviderMessageIdSchema,
  snoozeUntil: z.string().datetime({ offset: true }),
  reason: z.string().max(500).optional(),
});

/** Unsnooze one email's correspondence now, restoring any mail the snooze moved. */
export const UnsnoozeMessageSchema = BaseRequestSchema.extend({
  action: z.literal('unsnooze_message'),
  messageId: ProviderMessageIdSchema,
});
